import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Hero({ onOrder }) {
  return (
    <section
      id="home"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-end"
      data-testid="hero-section"
    >
      <img
        src="https://customer-assets.emergentagent.com/job_halli-chutney-site/artifacts/7y0zycg4_image_dfb7de5b.png"
        alt="Halli Chutney — traditional Indian spices"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 hero-mask" />
      <div className="absolute inset-0 grain" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24 pt-32 w-full">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full bg-stone-900/10 backdrop-blur-md border border-stone-900/15 px-4 py-1.5 text-stone-900 text-xs uppercase tracking-[0.2em]"
          >
            <Leaf className="w-3.5 h-3.5" />
            <span>Handcrafted in Karnataka</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-6 font-serif text-stone-900 text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight"
            data-testid="hero-headline"
          >
            Authentic <span className="italic text-[#993311]">Halli</span> Taste,
            <br />
            Delivered To Your Home.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-6 text-stone-800 text-base md:text-lg max-w-xl leading-relaxed"
          >
            Traditional village-style chutneys, slow-roasted in small batches with
            farm-fresh ingredients — the way our grandmothers made them in
            Karnataka.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Button
              onClick={onOrder}
              className="bg-[#993311] hover:bg-[#7A290E] text-stone-50 rounded-full h-14 px-8 text-base font-medium transition-transform hover:-translate-y-0.5 shadow-lg shadow-black/20"
              data-testid="hero-order-btn"
            >
              Order Now
            </Button>
            <a
              href="#shop"
              className="text-stone-900 text-base font-medium underline underline-offset-4 decoration-stone-900/40 hover:decoration-stone-900 transition-all"
              data-testid="hero-shop-link"
            >
              Explore our jars →
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-14 flex items-center gap-8 text-stone-800 text-sm"
          >
            <div>
              <div className="font-serif text-2xl md:text-3xl text-stone-900">100%</div>
              <div className="uppercase tracking-widest text-[10px] mt-1">
                Homemade
              </div>
            </div>
            <div className="w-px h-10 bg-stone-900/25" />
            <div>
              <div className="font-serif text-2xl md:text-3xl text-stone-900">4</div>
              <div className="uppercase tracking-widest text-[10px] mt-1">
                Village Recipes
              </div>
            </div>
            <div className="w-px h-10 bg-stone-900/25" />
            <div>
              <div className="font-serif text-2xl md:text-3xl text-stone-900">₹299</div>
              <div className="uppercase tracking-widest text-[10px] mt-1">
                Per Jar
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <a
        href="#about"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-stone-900/70 hover:text-stone-900 flex flex-col items-center gap-2 text-xs uppercase tracking-widest"
      >
        <span>Scroll</span>
        <ArrowDown className="w-4 h-4 animate-float-slow" />
      </a>
    </section>
  );
}
